import { useState, useEffect, useCallback } from 'react';

const DB_NAME = 'libritui_offline_voices';
const DB_VERSION = 1;
const STORE_NAME = 'voice_files';

// Exact R2 URLs for each language's voice files
const VOICE_URLS: Record<string, { model: string; config: string }> = {
  fr: {
    model: 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/Piper-TTS/voices/fr_FR/fr_FR-siwis-medium.onnx',
    config: 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/Piper-TTS/voices/fr_FR/fr_FR-siwis-medium.onnx.json',
  },
  en: {
    model: 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/Piper-TTS/voices/en_US/en_US-ryan-high.onnx',
    config: 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/Piper-TTS/voices/en_US/en_US-ryan-high.onnx.json',
  },
  pt: {
    model: 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/Piper-TTS/voices/pt_BR/pt_BR-cadu-medium.onnx',
    config: 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/Piper-TTS/voices/pt_BR/pt_BR-cadu-medium.onnx.json',
  },
};
const VOICE_FILE_KEYS = ['model.onnx', 'config.json'] as const;

// ── Cache Storage helpers ──
const CACHE_NAME = 'piper-voices-cache';

async function putFile(key: string, data: ArrayBuffer): Promise<void> {
  const cache = await caches.open(CACHE_NAME);
  const absoluteUrl = key.startsWith('http') ? key : `${window.location.origin}/piper-voices/${key}`;
  const response = new Response(data, {
    headers: {
      'Content-Type': key.endsWith('.json') ? 'application/json' : 'application/octet-stream',
      'Content-Length': data.byteLength.toString()
    }
  });
  await cache.put(absoluteUrl, response);
}

async function getFile(key: string): Promise<ArrayBuffer | null> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const absoluteUrl = key.startsWith('http') ? key : `${window.location.origin}/piper-voices/${key}`;
    const response = await cache.match(absoluteUrl);
    if (!response) return null;
    return await response.arrayBuffer();
  } catch {
    return null;
  }
}

async function deleteFile(key: string): Promise<void> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const absoluteUrl = key.startsWith('http') ? key : `${window.location.origin}/piper-voices/${key}`;
    await cache.delete(absoluteUrl);
  } catch {}
}

async function checkFilesExist(lang: string): Promise<boolean> {
  try {
    const cache = await caches.open(CACHE_NAME);
    const m1 = await cache.match(`${window.location.origin}/piper-voices/${lang}/model.onnx`);
    const m2 = await cache.match(`${window.location.origin}/piper-voices/${lang}/config.json`);
    return !!(m1 && m2);
  } catch {
    return false;
  }
}

// ── Download with progress ──

async function downloadWithProgress(
  url: string,
  onProgress: (loaded: number, total: number) => void
): Promise<ArrayBuffer> {
  const response = await fetch(url);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const contentLength = parseInt(response.headers.get('content-length') || '0');
  const reader = response.body!.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    loaded += value.length;
    onProgress(loaded, contentLength || loaded);
  }

  const result = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result.buffer;
}

// ── Public API for non-React contexts ──

export async function getVoiceFiles(lang: string): Promise<{
  model: ArrayBuffer; config: ArrayBuffer;
} | null> {
  try {
    const [model, config] = await Promise.all([
      getFile(`${lang}/model.onnx`),
      getFile(`${lang}/config.json`),
    ]);
    if (!model || !config) return null;
    return { model, config };
  } catch { return null; }
}

export async function isVoiceInstalledAsync(lang: string): Promise<boolean> {
  try { return await checkFilesExist(lang); } catch { return false; }
}

// ── React Hook ──

export interface VoiceStatus {
  installed: boolean;
  downloading: boolean;
  progress: number;
  error: string | null;
}

export function useOfflineVoices() {
  const [voices, setVoices] = useState<Record<string, VoiceStatus>>({});

  const updateVoice = useCallback((lang: string, patch: Partial<VoiceStatus>) => {
    setVoices(prev => ({
      ...prev,
      [lang]: { installed: false, downloading: false, progress: 0, error: null, ...prev[lang], ...patch }
    }));
  }, []);

  // Check installed voices on mount
  useEffect(() => {
    const check = async () => {
      for (const lang of ['fr', 'en', 'pt']) { // Iterate through all supported offline languages
        const installed = await isVoiceInstalledAsync(lang);
        updateVoice(lang, { installed });
      }
    };
    check();
  }, []);

  const downloadVoice = useCallback(async (lang: string) => {
    updateVoice(lang, { downloading: true, progress: 0, error: null });

    try {
      const urls = VOICE_URLS[lang];
      if (!urls) throw new Error(`No voice URLs configured for language: ${lang}`);

      // Download all 2 files sequentially with combined progress
      const entries: [string, string][] = [
        ['model.onnx', urls.model],
        ['config.json', urls.config],
      ];

      for (let i = 0; i < entries.length; i++) {
        const [key, url] = entries[i];
        const data = await downloadWithProgress(url, (loaded, total) => {
          const fileProgress = loaded / Math.max(total, 1);
          const overallProgress = ((i + fileProgress) / entries.length) * 100;
          updateVoice(lang, { downloading: true, progress: Math.round(overallProgress) });
        });
        await putFile(`${lang}/${key}`, data);
      }

      updateVoice(lang, { installed: true, downloading: false, progress: 100 });
    } catch (err: any) {
      console.error('[OfflineVoices] Download failed:', err);
      updateVoice(lang, { downloading: false, progress: 0, error: err.message || 'Download failed' });
    }
  }, [updateVoice]);

  const deleteVoice = useCallback(async (lang: string) => {
    try {
      for (const file of VOICE_FILE_KEYS) {
        await deleteFile(`${lang}/${file}`);
      }
      updateVoice(lang, { installed: false, progress: 0 });
    } catch (err: any) {
      console.error('[OfflineVoices] Delete failed:', err);
    }
  }, [updateVoice]);

  const isInstalled = useCallback((lang: string) => voices[lang]?.installed || false, [voices]);

  return { voices, isInstalled, downloadVoice, deleteVoice };
}
